// One line of the import preview, in full: what it is, where it sits in the tree, its
// quantity in each variant, what to check, and the files that came with it.
// apps/smart-catalog/frontend/src/pages/bom-intelligence/PreviewLineDetail.tsx

import React from 'react';
import { Box, ButtonBase, IconButton, InputBase, Tooltip, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import type { ColumnMapping, ParsedRow } from '../../types';
import { T } from '../../components/portfolio/PortfolioParts';
import { IBM_PLEX_MONO } from '../../theme/ibmPlex';
import type { BomConfiguration } from '../../components/bom-v2/ConfigurationSelect';
import { descendantsOf, isLostText, lineKind, lineNotes, quantityIn, type PreviewTree } from './preview-tree';

const caption = { fontSize: 11, fontWeight: 600, letterSpacing: '0.07em', textTransform: 'uppercase' as const, color: T.muted };
const KIND_LABEL = { assembly: 'Assembly', make: 'Make', buy: 'Buy', document: 'Document' } as const;
const same = (a: unknown, b: unknown) =>
  String(a ?? '').replace(/\s+/g, ' ').trim().toLowerCase() === String(b ?? '').replace(/\s+/g, ' ').trim().toLowerCase();

const FILE_KIND: Record<string, string> = { pdf: 'Drawing (PDF)', step: '3D model (STEP)', stp: '3D model (STEP)', x_t: '3D model (Parasolid)', igs: '3D model (IGES)', iges: '3D model (IGES)' };
const extOf = (fileName: string) => (fileName.split('.').pop() ?? '').toLowerCase();

/** The file column read as one of these fields, if the import has one. */
const columnFor = (mapping: ColumnMapping[], ...targets: string[]): string | undefined =>
  mapping.find((m) => targets.includes(m.targetField))?.sourceColumn;

/**
 * One cell of the file, changed in place: saved on Enter or when the field is
 * left, Esc puts it back. A changed cell shows the file's value with Undo.
 */
const EditableCell: React.FC<{
  label: string;
  value: string;
  /** The file's own value, when the cell was changed on the preview. */
  was?: unknown;
  problem?: string;
  variant?: 'title' | 'text' | 'field';
  numeric?: boolean;
  onSave: (value: string) => Promise<void>;
}> = ({ label, value, was, problem, variant = 'field', numeric, onSave }) => {
  const [draft, setDraft] = React.useState(value);
  const [saving, setSaving] = React.useState(false);
  const cancelled = React.useRef(false);
  React.useEffect(() => setDraft(value), [value]);
  const save = async () => {
    if (cancelled.current) { cancelled.current = false; setDraft(value); return; }
    if (draft.trim() === value.trim()) { setDraft(value); return; }
    setSaving(true);
    try { await onSave(draft); } catch { setDraft(value); } finally { setSaving(false); }
  };
  const multiline = variant === 'text';
  const ring = problem ? 'rgba(220, 38, 38, 0.12)' : 'rgba(30, 58, 95, 0.12)';
  return (
    <Box sx={{ display: 'grid', gap: 0.5, minWidth: 0 }}>
      <InputBase
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={save}
        onKeyDown={(e) => {
          if (e.key === 'Escape') { cancelled.current = true; (e.target as HTMLElement).blur(); }
          else if (e.key === 'Enter' && !(multiline && e.shiftKey)) { e.preventDefault(); (e.target as HTMLElement).blur(); }
        }}
        multiline={multiline}
        disabled={saving}
        inputProps={{ 'aria-label': label, dir: 'auto', inputMode: numeric ? 'decimal' : undefined }}
        sx={{
          mx: -1,
          px: 1,
          py: multiline ? 0.75 : 0,
          minHeight: { xs: 44, lg: variant === 'title' ? 38 : 32 },
          borderRadius: 2,
          border: '1px solid',
          borderColor: problem ? T.red : 'transparent',
          boxShadow: problem ? `0 0 0 3px ${ring}` : 'none',
          bgcolor: problem ? '#fff' : 'transparent',
          color: variant === 'text' ? T.ink2 : T.ink,
          fontSize: variant === 'title' ? 17 : variant === 'text' ? 14 : 13,
          fontWeight: variant === 'title' ? 700 : 400,
          lineHeight: 1.45,
          fontVariantNumeric: 'tabular-nums',
          transition: 'border-color 120ms, box-shadow 120ms',
          '&:hover': { borderColor: problem ? T.red : T.line },
          '&.Mui-focused': { borderColor: problem ? T.red : T.navy, boxShadow: `0 0 0 3px ${ring}`, bgcolor: '#fff' },
          '&.Mui-disabled': { opacity: 0.6 },
        }}
      />
      {problem && <Typography sx={{ fontSize: 12, color: '#B91C1C' }}>{problem}</Typography>}
      {was !== undefined && (
        <Typography component="div" sx={{ fontSize: 12, color: T.muted, overflowWrap: 'anywhere' }}>
          Was {String(was ?? '').trim() || 'empty'} ·{' '}
          <ButtonBase onClick={() => { void onSave(String(was ?? '')).catch(() => undefined); }} sx={{ fontSize: 12, color: T.link, verticalAlign: 'baseline', '&:hover': { textDecoration: 'underline' } }}>
            Undo
          </ButtonBase>
        </Typography>
      )}
    </Box>
  );
};

export const PreviewLineDetail: React.FC<{
  row: ParsedRow;
  rows: ParsedRow[];
  tree: PreviewTree;
  configurations?: BomConfiguration[];
  configurationKey?: string;
  currency?: string;
  /** Attach and remove the line's files (the table only lists them). */
  filesControl?: React.ReactNode;
  /** The import's column mapping: a field read from a column of the file can be changed. */
  columnMapping?: ColumnMapping[];
  /** Change cells of this line by column name. Absent = the line cannot be changed here. */
  onCellsChange?: (cells: Record<string, string>) => Promise<void>;
  onPrevious?: () => void;
  onNext?: () => void;
  onClose: () => void;
}> = ({ row, rows, tree, configurations, configurationKey, currency, filesControl, columnMapping, onCellsChange, onPrevious, onNext, onClose }) => {
  const md = row.mappedData ?? {};
  const kind = lineKind(row);
  const byIndex = new Map(rows.map((r) => [r.rowIndex, r]));
  const pn = String(md.partNumber ?? '');
  const description = !same(md.partDescription, pn) ? String(md.partDescription ?? '') : '';
  const drawing = md.drawingNumber && !same(md.drawingNumber, pn) && !same(md.drawingNumber, md.partDescription) ? String(md.drawingNumber) : '';
  const mpn = String(md.manufacturerPartNumber ?? md.mpn ?? '');
  const path: string[] = [];
  for (let p = tree.parentOf.get(row.rowIndex); p != null; p = tree.parentOf.get(p)) path.unshift(String(byIndex.get(p)?.mappedData?.partNumber ?? ''));
  const where = path.length ? path.join(' › ') : tree.unplaced.includes(row.rowIndex) ? 'Not on any assembly drawing' : 'Top of the bill';
  const notes = lineNotes(row);
  const docs = row.pendingDocuments ?? [];
  const price = Number(md.unitCost);
  const children = descendantsOf(tree, row.rowIndex).map((i) => byIndex.get(i)).filter((r): r is ParsedRow => !!r);

  // Fields read from a column of the file can be changed; the cell is shown as the file has it.
  const col = (...targets: string[]) => (onCellsChange && columnMapping?.length ? columnFor(columnMapping, ...targets) : undefined);
  const cellOf = (c: string) => String(row.rawData?.[c] ?? '');
  const wasOf = (c: string) => (row.originalCells && c in row.originalCells ? row.originalCells[c] : undefined);
  const saveTo = (c: string) => (value: string) => (onCellsChange as (cells: Record<string, string>) => Promise<void>)({ [c]: value });
  const pnCol = col('partNumber', 'localCode');
  const descCol = col('partDescription');
  const itemCol = col('itemNumber', 'findNumber', 'lineNumber');
  const qtyCol = col('quantity');
  const categoryCol = col('materialType');
  const makerCol = col('manufacturerName');
  const mpnCol = col('mpn', 'manufacturerPartNumber');
  const priceCol = col('unitCost');
  const quantityProblem = qtyCol && cellOf(qtyCol).trim() && !Number.isFinite(Number(md.quantity)) ? 'A number, like 12' : undefined;
  const quantity = quantityIn(row);
  const field = (label: string, c: string, opts: { problem?: string; numeric?: boolean } = {}) => (
    <>
      <dt>{label}</dt>
      <dd><EditableCell label={label} value={cellOf(c)} was={wasOf(c)} problem={opts.problem} numeric={opts.numeric} onSave={saveTo(c)} /></dd>
    </>
  );

  const section = { px: 2, py: 1.75, display: 'grid', gap: 1, borderTop: `1px solid ${T.soft}` };
  return (
    <Box sx={{ position: 'sticky', top: 12, bgcolor: '#fff', border: `1px solid ${T.line}`, borderRadius: 3, overflow: 'hidden', minWidth: 0 }}>
      <Box sx={{ px: 2, pt: 1.5, display: 'flex', alignItems: 'center' }}>
        <Box component="span" sx={{ height: 22, px: 1, borderRadius: 1.5, fontSize: 12, fontWeight: 600, display: 'inline-flex', alignItems: 'center', ...(kind === 'assembly' ? { border: `1px solid ${T.navy}`, color: T.navy } : { bgcolor: T.soft, color: T.ink2 }) }}>
          {KIND_LABEL[kind]}
        </Box>
        <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center' }}>
          {(onPrevious || onNext) && (
            <>
              <Tooltip title="Previous line" arrow><span><IconButton size="small" onClick={onPrevious} disabled={!onPrevious} aria-label="Previous line" sx={{ color: T.muted }}><KeyboardArrowUpIcon sx={{ fontSize: 20 }} /></IconButton></span></Tooltip>
              <Tooltip title="Next line" arrow><span><IconButton size="small" onClick={onNext} disabled={!onNext} aria-label="Next line" sx={{ color: T.muted }}><KeyboardArrowDownIcon sx={{ fontSize: 20 }} /></IconButton></span></Tooltip>
            </>
          )}
          <IconButton size="small" onClick={onClose} aria-label="Close" sx={{ color: T.muted }}>
            <CloseIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>
      </Box>
      <Box sx={{ px: 2, pt: 1, pb: 1.75, display: 'grid', gap: 0.25 }}>
        {pnCol ? (
          <EditableCell label="Part number" variant="title" value={cellOf(pnCol)} was={wasOf(pnCol)} onSave={saveTo(pnCol)} />
        ) : (
          <Typography sx={{ fontSize: 17, fontWeight: 700, color: T.ink, overflowWrap: 'anywhere', fontVariantNumeric: 'tabular-nums' }}>{pn}</Typography>
        )}
        {descCol ? (
          <EditableCell label="Description" variant="text" value={cellOf(descCol)} was={wasOf(descCol)} onSave={saveTo(descCol)} />
        ) : (
          description && <Typography dir="auto" sx={{ fontSize: 14, color: T.ink2 }}>{description}</Typography>
        )}
      </Box>
      <Box
        component="dl"
        sx={{
          ...section,
          m: 0,
          gridTemplateColumns: '104px minmax(0, 1fr)',
          columnGap: 1.5,
          rowGap: onCellsChange ? 0.5 : 0.875,
          alignItems: 'start',
          fontSize: 13,
          '& dt': { color: T.muted, ...(onCellsChange ? { minHeight: { xs: 44, lg: 32 }, display: 'flex', alignItems: 'center' } : {}) },
          '& dd': { m: 0, minWidth: 0, overflowWrap: 'anywhere', color: T.ink, ...(onCellsChange ? { minHeight: { xs: 44, lg: 32 }, display: 'flex', flexDirection: 'column', justifyContent: 'center' } : {}) },
        }}
      >
        {drawing && (<><dt>Drawing</dt><dd>{drawing}</dd></>)}
        {itemCol && field('Item no.', itemCol)}
        {qtyCol
          ? field('Quantity', qtyCol, { problem: quantityProblem, numeric: true })
          : !configurations?.length && kind !== 'assembly' && quantity != null && (<><dt>Quantity</dt><dd style={{ fontVariantNumeric: 'tabular-nums' }}>{quantity.toLocaleString()}</dd></>)}
        <dt>In</dt><dd>{where}</dd>
        {categoryCol
          ? field('Category', categoryCol)
          : md.materialType && (<><dt>Category</dt><dd>{isLostText(md.materialType) ? <span style={{ color: T.faint }}>Unreadable in the file ({String(md.materialType)})</span> : String(md.materialType)}</dd></>)}
        {makerCol
          ? field('Maker', makerCol)
          : md.manufacturerName && (<><dt>Maker</dt><dd>{String(md.manufacturerName)}</dd></>)}
        {mpnCol ? field('Maker part no.', mpnCol) : mpn && (<><dt>Maker part no.</dt><dd>{mpn}</dd></>)}
        {priceCol
          ? field(currency ? `Unit price (${currency})` : 'Unit price', priceCol, { numeric: true })
          : Number.isFinite(price) && price > 0 && (<><dt>Unit price</dt><dd style={{ fontFamily: IBM_PLEX_MONO }}>{price.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} {currency ?? ''}</dd></>)}
      </Box>
      {kind !== 'assembly' && configurations?.length ? (
        <Box sx={section}>
          <Typography sx={caption}>Quantity per variant</Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: `repeat(${configurations.length}, minmax(0, 1fr))`, border: `1px solid ${T.line}`, borderRadius: 2.5, overflow: 'hidden' }}>
            {configurations.map((c, i) => (
              <Box key={c.key} sx={{ px: 1.25, py: 1, display: 'grid', gap: 0.25, borderLeft: i ? `1px solid ${T.line}` : 0, bgcolor: c.key === configurationKey ? '#F2F6FB' : 'transparent' }}>
                <Typography sx={{ ...caption, fontSize: 10 }} noWrap>{c.label}</Typography>
                <Typography sx={{ fontFamily: IBM_PLEX_MONO, fontSize: 15 }}>{quantityIn(row, c.key) ?? '–'}</Typography>
              </Box>
            ))}
          </Box>
        </Box>
      ) : null}
      {kind === 'assembly' && (
        <Box sx={section}>
          <Typography sx={caption}>Contains</Typography>
          <Typography sx={{ fontSize: 13, color: T.ink2 }}>
            {children.filter((c) => lineKind(c) === 'assembly').length} sub-assemblies · {children.filter((c) => lineKind(c) !== 'assembly').length} parts
          </Typography>
        </Box>
      )}
      {notes.length > 0 && (
        <Box sx={section}>
          <Typography sx={caption}>Notes</Typography>
          {notes.map((n, i) => (
            <Box key={i} sx={{ border: '1px solid', borderColor: n.error ? '#FECACA' : n.info ? T.line : '#FDE68A', bgcolor: n.error ? '#FEF2F2' : n.info ? T.bg : '#FFFBEB', color: n.error ? '#7F1D1D' : n.info ? T.ink2 : '#78350F', borderRadius: 2.5, px: 1.5, py: 1.25, display: 'grid', gap: 0.5, fontSize: 13 }}>
              <Box component="b" sx={{ fontWeight: 600 }}>{n.short}</Box>
              {n.text !== n.short && <span>{n.text}</span>}
            </Box>
          ))}
        </Box>
      )}
      <Box sx={section}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography sx={caption}>Files</Typography>
          {filesControl && <Box sx={{ ml: 'auto' }}>{filesControl}</Box>}
        </Box>
        {docs.length ? (
          docs.map((d) => (
            <Box key={d.documentId} sx={{ display: 'flex', alignItems: 'center', gap: 1, fontSize: 13, minWidth: 0 }}>
              <Box component="span" sx={{ fontFamily: IBM_PLEX_MONO, fontSize: 10.5, color: T.muted, border: `1px solid ${T.line}`, borderRadius: 1.25, px: 0.625, width: 44, textAlign: 'center', flex: 'none' }}>
                {extOf(d.fileName).toUpperCase()}
              </Box>
              <Box component="span" sx={{ color: T.ink2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={d.fileName}>
                {FILE_KIND[extOf(d.fileName)] ?? d.fileName}
              </Box>
            </Box>
          ))
        ) : (
          <Typography sx={{ fontSize: 13, color: T.muted }}>No file in the package for this line.</Typography>
        )}
      </Box>
    </Box>
  );
};

export default PreviewLineDetail;
